import { TimeOption, buildTimeOptions } from '@utils';
import { Col, Select, Typography } from 'antd';
import React from 'react';

const { Text } = Typography;

export interface TimeRangeValue {
  startTime?: string;
  endTime?: string;
}

const fullDayOptions = buildTimeOptions(15);

interface IProps {
  value?: TimeRangeValue;
  options?: TimeOption[];
  disabled?: boolean;
  placeholder?: [string, string];
  /**
   * Work-log mode: the end may be left empty (still running) and may be any time of day - one
   * before the start means the next day (a night job). Off by default: a booked slot or an
   * unavailability always has an end on the same day, so picking a start fills one in and the
   * end is kept after it.
   */
  allowOpenEnd?: boolean;
  onChange?: (value: TimeRangeValue) => void;
}

/**
 * Two selects for picking a start and an end time of the same day.
 * Times outside `options` cannot be picked, and the end time is always after the start time.
 */
export const TimeRangeSelect: React.FC<IProps> = ({
  value,
  options = fullDayOptions,
  disabled,
  placeholder = ['Start time', 'End time'],
  allowOpenEnd = false,
  onChange,
}) => {
  const { startTime, endTime } = value ?? {};

  // the last option can only ever be an end time (unless the range may wrap past midnight)
  const startOptions = allowOpenEnd ? options : options.slice(0, -1);
  const endOptions = allowOpenEnd
    ? options
    : options.filter((option) => !startTime || option.value > startTime);

  const handleStartChange = (nextStartTime: string) => {
    if (allowOpenEnd) {
      // The end is the user's to set (or leave open); an earlier one simply means the next day.
      onChange?.({ startTime: nextStartTime, endTime });
      return;
    }
    const keepsOrder = endTime && endTime > nextStartTime;
    onChange?.({
      startTime: nextStartTime,
      endTime: keepsOrder ? endTime : options.find((option) => option.value > nextStartTime)?.value,
    });
  };

  const handleEndChange = (nextEndTime: string | undefined) => {
    onChange?.({ startTime, endTime: nextEndTime });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Col flex="auto" className="time-range-select">
        <Select
          style={{ width: '100%', minWidth: '100px' }}
          options={startOptions}
          value={startTime ?? null}
          placeholder={placeholder[0]}
          disabled={disabled}
          onChange={handleStartChange}
        />
      </Col>
      <Text>to</Text>
      <Col flex="auto" className="time-range-select">
        <Select
          style={{ width: '100%', minWidth: '100px' }}
          options={endOptions}
          value={endTime ?? null}
          placeholder={placeholder[1]}
          disabled={disabled || !startTime}
          allowClear={allowOpenEnd}
          onChange={handleEndChange}
        />
      </Col>
    </div>
  );
};
